/**
 * ReviewListToolbar
 *
 * The "Select All" bar + optional token-usage header that appears above every
 * review list in both wizards.  Identical markup exists in:
 *   - JiraReviewScreen        (test-case review)
 *   - DocumentToJiraWizard    ReviewStoriesScreen  (story review)
 *   - DocumentToJiraWizard    ReviewTCsScreen      (TC review in Jira wizard)
 *
 * Props
 * ─────
 * allSelected     bool      – Whether every item is currently selected
 * someSelected    bool      – Whether at least one (but not all) item is selected
 * selectedCount   number    – Number of currently-selected items
 * totalCount      number    – Total number of items in the list
 * onToggleAll     fn        – Callback when the Select-All checkbox changes
 * disabled        bool      – Disables the checkbox (e.g. during save)
 * warning         node      – Optional JSX rendered on the right side of the bar
 *                             (e.g. "N selected require tech layer" badge)
 * tokenUsage      object    – { total_tokens, prompt_tokens, output_tokens }
 *                             When provided, renders a token-usage line above the bar.
 *                             The two wizards use different key names so we
 *                             also accept { total, input, output } (Jira wizard shape).
 * showLegend      bool      – When true renders Module/Label colour-dot legend
 *                             (used by JiraReviewScreen). Default false.
 * legendItems     array     – [{ color, label }] overrides the default legend dots.
 *
 * Usage examples
 * ──────────────
 * // JiraReviewScreen
 * <ReviewListToolbar
 *   allSelected={allSelected}
 *   someSelected={someSelected}
 *   selectedCount={selectedCount}
 *   totalCount={tcs.length}
 *   onToggleAll={toggleSelectAll}
 *   disabled={submitting}
 *   showLegend
 *   tokenUsage={tokenUsage}
 *   warning={selectedWithNoTechLayer.length > 0 && (
 *     <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
 *       <WarningAmberIcon sx={{ fontSize:14, color:'warning.main' }} />
 *       <Typography variant="caption" color="warning.dark" fontWeight={500}>
 *         {selectedWithNoTechLayer.length} selected require tech layer
 *       </Typography>
 *     </Box>
 *   )}
 * />
 *
 * // ReviewStoriesScreen  (DocumentToJiraWizard)
 * <ReviewListToolbar
 *   allSelected={allSelected}
 *   someSelected={!allSelected && selectedCount > 0}
 *   selectedCount={selectedCount}
 *   totalCount={stories.length}
 *   onToggleAll={toggleAll}
 *   tokenUsage={tokenUsage}  // { total, input, output } shape also accepted
 * />
 */

import { Box, Checkbox, Chip, Typography } from '@mui/material';

const DEFAULT_LEGEND = [
  { color: '#4338ca', label: 'Module' },
  { color: '#165c33', label: 'Label' },
];

// Normalise both token-usage object shapes into { total, input, output }
const normaliseTokenUsage = (tu) => {
  if (!tu) return null;
  // JiraReviewScreen / AddTestCaseModal shape: total_tokens, prompt_tokens, output_tokens
  if (tu.total_tokens !== undefined) {
    return {
      total: tu.total_tokens,
      input: tu.prompt_tokens,
      output: tu.output_tokens,
    };
  }
  // DocumentToJiraWizard shape: total, input, output
  return tu;
};
const LegendDots = ({ items }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    {items.map(({ color, label }) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const ReviewListToolbar = ({
  allSelected,
  someSelected,
  selectedCount,
  totalCount,
  onToggleAll,
  disabled = false,
  warning,
  tokenUsage,
  showLegend = false,
  legendItems = DEFAULT_LEGEND,
}) => {
  const tu = normaliseTokenUsage(tokenUsage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Token usage row */}
      {tu && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Tokens used:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {tu.total?.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              ({tu.input?.toLocaleString()} in · {tu.output?.toLocaleString()} out)
            </Typography>
          </Box>

          {showLegend && <LegendDots items={legendItems} />}
        </Box>
      )}

      {/* Select-all bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 1,
          bgcolor: '#f9fafb',
          borderRadius: 1,
          border: '1px solid #e5e7eb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={onToggleAll}
            disabled={disabled}
            inputProps={{ 'aria-label': 'Select all items' }}
          />
          <Typography variant="body2" color="text.secondary">
            Select All
          </Typography>
          <Chip
            label={`${selectedCount} selected`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, fontSize: '11px' }}
          />
          <Typography variant="caption" color="text.secondary">
            of {totalCount}
          </Typography>
        </Box>

        {/* Right-side slot — warnings, legend without tokenUsage, etc. */}
        {warning && <Box>{warning}</Box>}

        {/* Legend rendered inline only when there is no token-usage header row */}
        {showLegend && !tu && <LegendDots items={legendItems} />}
      </Box>
    </Box>
  );
};

export default ReviewListToolbar;